import React, { useState, useEffect } from 'react'
import navData from './navLinks.json'
import Link from 'next/link'
import Image from 'next/image'
import styles from './MegaNav.module.scss'
import navLogo from '@/public/assets/images/nav_logo_zscaler.svg'

const isExternalHref = (href: string) =>
  href.startsWith('http://') || href.startsWith('https://')

interface HeaderProps {
  onSearchOpen: () => void
}
const MegaNav = ({ onSearchOpen }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [openAccordion, setOpenAccordion] = useState<string | number | null>(null)

  // handle body scroll lock when mobile menu is open
  useEffect(() => {
    const updateBodyOverflow = () => {
      // Check both conditions: menu open AND screen is "tablet or below"
      if (isMobileMenuOpen && window.innerWidth < 1024) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
    }

    updateBodyOverflow()

    // event listener to run the check on window resize
    window.addEventListener('resize', updateBodyOverflow)

    return () => {
      // Remove the event listener when the component unmounts or when isMobileMenuOpen changes
      window.removeEventListener('resize', updateBodyOverflow)

      // Always reset the overflow to 'auto' on cleanup
      document.body.style.overflow = 'auto'
    }
  }, [isMobileMenuOpen])

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setOpenAccordion(null) // Reset accordion on menu close
  }

  const toggleAccordion = (id: string | number) => {
    setOpenAccordion(openAccordion === id ? null : id)
  }

  // SVG for Chevron Icon
  const ChevronDownIcon = ({ className }: { className?: string }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  )

  const HamburgerIconCollapsed = () => (
    <svg
      width="21"
      height="21"
      viewBox="0 0 25 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`h-6 w-6 inline!`}
      stroke="currentColor"
    >
      <path
        d="M1.14282 6.7143L5.42853 11L1.14282 15.2857"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 1H24"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.1428 11H24"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 21H24"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  const HamburgerIconExpanded = () => (
    // pink color
    <svg
      width="21"
      height="21"
      viewBox="0 0 25 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`h-6 w-6 !inline scale-x-[-1] ${styles.mobileHamburgerOpened}`}
      stroke="currentColor"
    >
      <path
        d="M1.14282 6.7143L5.42853 11L1.14282 15.2857"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 1H24"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.1428 11H24"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 21H24"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )

  return (
    <div className={` text-white shadow-md relative z-50 ${styles.meganav}`}>
      <div
        className={`${styles.container} mx-auto px-6 lg:px-8 z-9 bg-[#1b2531] ${styles.meganavDesktop}`}
      >
        <div
          className={`flex items-center justify-between h-[66px] md:h-[70px] lg:h-[90px] ${styles.metaNav}`}
        >
          {/* Logo */}
          <div className="shrink-0">
            <Link href="/" className={`flex items-center space-x-`}>
              <span className={`font-bold text-xl tracking-wider`}>
                <Image src={navLogo} alt="Zscaler" />
              </span>
              <span
                className={`text-gray-400 font-light text-[20.5px] md:text-[23.75px] ${styles.navThreatLabz}`}
              >
                ThreatLabz
              </span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search Button */}
            <button
              type="button"
              className={`${styles.btnSearchSite} hidden lg:block bg-gray-700 hover:bg-gray-600 text-white text-[14px] font-normal leading-[17.5px] p-[15px] transition-colors duration-200 cursor-pointer`}
              onClick={onSearchOpen}
            >
              <span className="text-bright-red mr-1">/</span>
              SEARCH
            </button>
            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className="cursor-pointer inline-flex items-center justify-center p-2 text-gray-400 hover:text-white focus:outline-none transition-colors duration-200"
              >
                <span className="sr-only">Open main menu</span>
                <div
                  className={`hamburgerIconWrapper w-[21px] h-[22px] overflow-hidden ${isMobileMenuOpen ? styles.isMobileMenuOpen : ''}`}
                >
                  {/* hamburger expand and collapse will animate this sprite left and right */}
                  <div className={styles.hamburgerSprite}>
                    <HamburgerIconCollapsed />
                    <HamburgerIconExpanded />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
        <div
          className={`hidden lg:flex items-center justify-between h-12 ${styles.navigationDesktop}`}
        >
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-10">
            {navData.navLinks.map((item) => (
              <div key={item.id} className="relative group">
                {item.href && (
                  <a
                    href={item.href}
                    className="text-[14px]! flex items-center space-x-2 text-sm font-medium text-gray-300! hover:text-[#e53e3e]! transition-colors duration-200 uppercase! tracking-wider py-1"
                    {...(isExternalHref(item.href)
                      ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                      : {})}
                  >
                    <span>{item.title}</span>
                    <ChevronDownIcon className="w-4 h-4 transition-transform duration-200 group-hover:rotate-270" />
                  </a>
                )}
                {!item.href && (
                  <>
                    <button className="text-[14px]! flex items-center space-x-2 text-sm font-medium text-gray-300! hover:text-[#e53e3e]! transition-colors duration-200 uppercase! tracking-wider py-1">
                      <span>{item.title}</span>
                      <ChevronDownIcon className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
                    </button>
                    {/* Desktop Dropdown */}
                    <div
                      className={`absolute top-full left-0 mt-2 w-max min-w-60 shadow-lg outline-none pt-2 pb-2 transition-all duration-300 ease-out transform origin-top group-hover:opacity-100 group-hover:scale-y-100 group-hover:translate-y-0 opacity-0 scale-y-0 -translate-y-2 ${styles.desktopDropDown}`}
                    >
                      {/* invisible bridge to gap between main categories and expanded menu below -- otherwise hover out will close the submenus */}
                      <div className="absolute h-4 w-full -top-4"></div>
                      <div
                        className="py-0"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                      >
                        {item.menuColumns
                          .flatMap((col) => col.links)
                          .map((link) => (
                            <Link
                              key={link.id}
                              href={link.href}
                              className={`block px-6 py-3 text-sm uppercase font-medium ${styles.subnavBtn}`}
                              role="menuitem"
                              {...(isExternalHref(link.href)
                                ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                                : {})}
                            >
                              <span className={styles.linkTitle}>{link.title}</span>
                            </Link>
                          ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`fixed px-2 left-0 w-full bg-[#1b2531] transition-transform duration-500 ease-in-out lg:hidden ${styles.meganavMobile} ${
          isMobileMenuOpen ? styles.isMobileMenuOpen : ''
        }`}
      >
        <div className="pt-5 pb-6 px-4 space-y-1">
          {navData.navLinks.map((item) => (
            // sub-item link
            <div key={item.id} className="border-b border-gray-700 last:border-b-0">
              {item.href && (
                <a
                  className="w-full flex items-center py-4 text-left text-base font-medium text-gray-300! hover:text-white!"
                  href={item.href}
                  {...(isExternalHref(item.href)
                    ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                    : {})}
                >
                  <span
                    className={`uppercase tracking-wider mr-2.5 transition duration-300 ${openAccordion === item.id ? 'text-red-500' : ''}`}
                  >
                    {item.title}
                  </span>
                  <ChevronDownIcon
                    className={`w-5 h-5 transform transition-transform duration-300 rotate-270 ${openAccordion === item.id ? ' rotate-270 text-red-500' : 'text-gray-400'}`}
                  />
                </a>
              )}
              {!item.href && (
                <>
                  <button
                    onClick={() => toggleAccordion(item.id)}
                    className="w-full flex items-center py-4 text-left text-base font-medium text-gray-300! hover:text-white!"
                  >
                    <span
                      className={`uppercase tracking-wider mr-2.5 transition duration-300 ${openAccordion === item.id ? 'text-red-500' : ''}`}
                    >
                      {item.title}
                    </span>
                    <ChevronDownIcon
                      className={`w-5 h-5 transform transition-transform duration-300 ${openAccordion === item.id ? ' rotate-180 text-red-500' : 'text-gray-400'}`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      openAccordion === item.id ? 'max-h-screen' : 'max-h-0'
                    }`}
                  >
                    <div className="pl-4 pb-4 pt-2">
                      {item.menuColumns
                        .flatMap((col) => col.links) // flatten multiple mappings down by one level to use single mapping instead of chaining maps
                        .map((link) => (
                          <Link
                            key={link.id}
                            href={link.href}
                            className={`block py-2 text-sm uppercase tracking-wider ${styles.subnavBtn}`}
                            onClick={toggleMobileMenu}
                            {...(isExternalHref(link.href)
                              ? { target: '_blank' as const, rel: 'noopener noreferrer' }
                              : {})}
                          >
                            <>
                              <span className={styles.linkTitle}>{link.title}</span>
                            </>
                          </Link>
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
          <div className="pt-8">
            <button
              type="button"
              className={`${styles.btnSearchSite} w-full bg-gray-700 hover:bg-gray-600 text-white text-[14px] font-normal leading-[17.5px] p-[15px] transition-colors duration-200 cursor-pointer`}
              onClick={onSearchOpen}
            >
              <span className="text-bright-red mr-1">/</span>
              SEARCH
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MegaNav
